import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';
import { Agent, AgentService } from 'src/app/apis/agent.service';
import { AppCore } from 'src/app/utils/classes/app-core';

export interface AgentDisplay extends Agent {
  agent_name_highlighted: string;
  agent_contact_name_highlighted: string;
}

@Component({
  selector: 'app-choose-agent-offcanvas',
  templateUrl: './choose-agent-offcanvas.component.html',
  styleUrls: ['./choose-agent-offcanvas.component.scss'],
})
export class ChooseAgentOffcanvasComponent extends AppCore implements OnInit {
  constructor(private readonly svAgent: AgentService) {
    super();

    const filterItem$ = this.filterItems$.pipe(debounceTime(400)).subscribe(() => {
      this.onSearchAgent();
    });
    this.Subscriptions.add(filterItem$);
  }

  ngOnInit(): void {
    this.getAgents();
    const offcanvasElement = document.querySelector('#chooseAgentOffcanvas');
    if (offcanvasElement) {
      offcanvasElement.addEventListener('show.bs.offcanvas', () => {
        this.scrollToCurrentAgent();
      });
    }
  }

  scrollToCurrentAgent(): void {
    if (this.selectedAgent) {
      document
        .querySelector(`#agent-item-${this.selectedAgent.id}-${this.selectedAgent.agent_code}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  public agentItems: AgentDisplay[] = [];
  private allAgentItems: Agent[] = [];
  public agentLoading = false;

  getAgents() {
    this.agentLoading = true;
    const params = {
      limit: 200,
    };
    this.svAgent
      .getAll(params)
      .pipe(
        finalize(() => {
          this.agentLoading = false;
        })
      )
      .subscribe({
        next: (res) => {
          this.allAgentItems = res.items || [];
          this.agentItems = this.allAgentItems.map((agent) => ({
            ...agent,
            agent_name_highlighted: agent.agent_name || '-',
            agent_contact_name_highlighted: agent.agent_contact_name || '-',
          }));
        },
      });
  }

  trackByAgent(index: number, agent: Agent): number {
    return agent.id;
  }

  onSelectAgent(agent: Agent) {
    this.selectedAgent = { ...agent };
  }

  public selectedAgent: Agent | undefined;
  currentAgentChanged(): void {
    this.selectedAgent = { ...this.currentAgent };
  }

  public filterOptions = {
    caseSensitive: false,
    matchFromStart: false,
    equals: false,
  };
  public filterText = '';
  private readonly filterItems$ = new Subject<void>();

  onFilterOptionsChange(): void {
    if (!this.filterText) {
      return;
    }

    this.onSearchAgent();
  }

  onFilterTextChange() {
    this.filterItems$.next();
  }

  onResetFilter() {
    this.filterText = '';
    this.filterOptions = {
      caseSensitive: false,
      matchFromStart: false,
      equals: false,
    };
    this.onSearchAgent();
  }

  onSearchAgent() {
    const searchText = this.filterText.trim();

    if (!searchText) {
      this.agentItems = this.allAgentItems.map((agent) => ({
        ...agent,
        agent_name_highlighted: agent.agent_name || '-',
        agent_contact_name_highlighted: agent.agent_contact_name || '-',
      }));
      return;
    }

    const { caseSensitive, matchFromStart, equals } = this.filterOptions;
    const flags = caseSensitive ? 'g' : 'gi';
    const escapedSearch = searchText.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
    const regex = new RegExp(`(${escapedSearch})`, flags);

    const highlightMatch = (text: string | undefined, isExactMatch: boolean): string => {
      if (!text) return '-';
      if (equals && isExactMatch) {
        return `<mark>${text}</mark>`;
      }
      return text.replace(regex, '<mark>$1</mark>');
    };

    const isMatch = (text: string | undefined): boolean => {
      if (!text) return false;
      const textToSearch = caseSensitive ? text : text.toLowerCase();
      const search = caseSensitive ? searchText : searchText.toLowerCase();
      if (equals) {
        return textToSearch === search;
      }
      return matchFromStart ? textToSearch.startsWith(search) : textToSearch.includes(search);
    };

    this.agentItems = this.allAgentItems
      .filter((agent) => isMatch(agent.agent_name) || isMatch(agent.agent_contact_name))
      .map((agent) => ({
        ...agent,
        agent_name_highlighted: highlightMatch(agent.agent_name, isMatch(agent.agent_name)),
        agent_contact_name_highlighted: highlightMatch(agent.agent_contact_name, isMatch(agent.agent_contact_name)),
      }));
  }

  @Output() submitAgent = new EventEmitter<Agent>();
  public onSubmit() {
    if (!this.selectedAgent || this.agentLoading) {
      return;
    }
    this.submitAgent.emit(this.selectedAgent);
    this.closeOffcanvas();
  }

  closeOffcanvas() {
    const closeButton = document.querySelector('#chooseAgentOffcanvas button.btn-close') as
      | HTMLButtonElement
      | undefined;
    closeButton?.click();
  }
}
